/* eslint-disable lines-between-class-members */

import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";
import { sanitizeUrl } from "@braintree/sanitize-url";

const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const ctor = typeof globalThis !== "undefined" && (globalThis.LinkNode || globalThis.Link);
const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

const LINK_PROPS = ["href", "networkable"];

/* -------------------------------------------------------------------------- */
/*  Editor adapter                                                             */
/* -------------------------------------------------------------------------- */

class LinkEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    LINK_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v); // Store original value in adapter
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(this.sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              this.sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed from source without invoking setters when possible
      const desc = originalDescriptors.get(prop);
      let initial;
      try {
        if (desc?.get) initial = desc.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* -------------------------------------------------------------------------- */
/*  A-Frame adapter                                                            */
/* -------------------------------------------------------------------------- */

class LinkAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;
    const COMPONENT = "media-loader";
    const pending = (this._pendingUpdates = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      if (pending[COMPONENT]) {
        const current = { ...(el.getAttribute(COMPONENT) || {}) };
        Object.assign(current, pending[COMPONENT]);
        
        // Handle mediaOptions separately
        if (pending.mediaOptions) {
          current.mediaOptions = {
            ...(current.mediaOptions || {}),
            ...pending.mediaOptions
          };
          delete pending.mediaOptions;
        }
        
        el.setAttribute(COMPONENT, current);
        delete pending[COMPONENT];
      }
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      const name = e?.detail?.name;
      if (name === COMPONENT) flush();
    });

    const setMediaLoaderProp = (k, v, inMediaOptions = false) => {
      const el = this.sourceObject?.el;
      if (!el) {
        if (inMediaOptions) {
          (pending.mediaOptions ||= {})[k] = v;
        } else {
          (pending[COMPONENT] ||= {})[k] = v;
        }
        return;
      }
      
      const attr = el.getAttribute(COMPONENT);
      if (!attr) {
        if (inMediaOptions) {
          (pending.mediaOptions ||= {})[k] = v;
        } else {
          (pending[COMPONENT] ||= {})[k] = v;
        }
      } else {
        const next = { ...attr };
        if (inMediaOptions) {
          next.mediaOptions = { ...(next.mediaOptions || {}), [k]: v };
        } else {
          next[k] = v;
        }
        el.setAttribute(COMPONENT, next);
      }
    };

    const getMediaLoaderProp = (k, inMediaOptions = false) => {
      const el = this.sourceObject?.el;
      if (!el) {
        return inMediaOptions ? pending.mediaOptions?.[k] : pending[COMPONENT]?.[k];
      }
      const attr = el.getAttribute(COMPONENT);
      if (attr) {
        return inMediaOptions ? attr.mediaOptions?.[k] : attr[k];
      }
      return inMediaOptions ? pending.mediaOptions?.[k] : pending[COMPONENT]?.[k];
    };

    // href in mediaOptions
    Object.defineProperty(this, "href", {
      get: () => getMediaLoaderProp("href", true),
      set: v => setMediaLoaderProp("href", v, true)
    });

    // networkable boolean attribute
    Object.defineProperty(this, "networkable", {
      get: () => !!this.sourceObject?.el?.hasAttribute("networked"),
      set: v => {
        const el = this.sourceObject?.el;
        if (!el) return;
        if (v) {
          if (!el.hasAttribute("networked")) {
            el.setAttribute("networked", {
              template: "#static-controlled-media"
            });
          }
        } else {
          el.removeAttribute("networked");
        }
      }
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of LINK_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  // A-Frame path if we have an element
  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new LinkAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  // Editor / plain Three path
  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", {
    value: new LinkEditorAdapter(self, descriptors),
    configurable: true
  });
  return self._adapter;
}



// DRY helper: upgrade an editor LinkNode to scriptable and attach adapter + proxies
function makeEditorLinkNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || ctor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) 
      Object.defineProperty(node, "scriptableType", { value: LinkElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = LinkElement.scriptableType;
  }

  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new LinkEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new LinkEditorAdapter(node, descriptors);
  }

  for (const prop of LINK_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* best-effort */
    }
  }

  // Ensure LinkElement's enhanced `clone` logic is used for
  // editor-created link nodes. Without this, the instance would fall back to
  // LinkNode (or Object3D) defaults which do not propagate the
  // scriptable properties managed by the adapter.
  try {
    if (node.clone !== LinkElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: LinkElement.prototype.clone,
        writable: true,
        configurable: true
      });
    }
  } catch {
    node.clone = LinkElement.prototype.clone;
  }

  return node;
}

// Editor factory for Link (editor/plain Three)
function createLinkElementEditor(initial = {}) {
  const Ctor = globalThis.LinkNode || globalThis.Link;
  if (typeof Ctor !== "function") {
    throw new Error("LinkElementEditor: LinkNode constructor not found.");
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new Ctor(editor);
  } catch (e) {
    throw new Error(`LinkElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorLinkNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) {
      node[k] = initial[k];
    }
  }
  
  return node;
}

export class LinkElement extends ScriptableObject {
  static scriptableType = "link";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");

      if (initial.networkable) {
        el.setAttribute("networked", {
          template: "#static-controlled-media"
        });
      }

      // Add media-loader component with link configuration
      const mediaOptions = {
        href: sanitizeUrl(initial.href || ""),
        hideHref: initial.hideHref,
        isIframeLink: initial.isIframeLink,
        smoothTransition: initial.smoothTransition
      };

      el.setAttribute("media-loader", {
        src: sanitizeUrl(initial.href || ""),
        fitToBox: false,
        resolve: true,
        fileIsOwned: true,
        animate: false,
        mediaOptions,
        moveTheParentNotTheMesh: true,
        useFancyLoader: false
      });

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real LinkNode and upgrade to scriptable
    const node = createLinkElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    console.log("Copying link element", source, this);

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      
      // deep-clone where necessary to avoid shared refs
      if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else {
        this[prop] = val;
      }
    };

    for (const p of LINK_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const child of logicalChildrenOf(source)) {
        this.add(child.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of LINK_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      // Follow the same pattern as other implementations
      if (Array.isArray(v)) {
        init[p] = v.slice();
      } else {
        init[p] = v;
      }
    }

    const dest = new LinkElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// If you create objects via adopt(el) (constructor doesn't run),
// the accessor getter calls ensureAdapter(this) and mounts the adapter on first access.
// That's why binding these getters on the prototype is crucial—even for adopted instances.
LINK_PROPS.forEach(prop => {
  Object.defineProperty(LinkElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

ScriptableObjects.registerType("link", source => {
  if (!source) return source;

  // Only promote if type matches (brand can live on obj or on el.object3D)
  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;
  if (type !== LinkElement.scriptableType) return source;

  // If given an A-Frame element or a Scriptable with .el -> adopt that element
  if (isAFrameEntity(source)) return LinkElement.adopt(source);

  // Editor/plain-Three: upgrade in-place to scriptable and attach adapter + proxies
  if (isObject3D(source)) return makeEditorLinkNodeScriptable(source);

  return source;
});

// Named exports expected by external consumers -----------------------------
export { LinkAFrameAdapter, LinkEditorAdapter, LINK_PROPS };
